import { uniqueId } from "@hypertune/sdk/src/shared";
import { Expression } from "@hypertune/sdk/src/shared/types";
import {
  ExpressionMap,
  ExpressionMapPointer,
  ExpressionMapValue,
} from "./types";

export default function getExpressionFromMap(
  exprMap: ExpressionMap,
  fallbackExprMap: ExpressionMap,
  pointer: ExpressionMapPointer | null
): Expression | null {
  if (!pointer) {
    return null;
  }
  const exprMapValue = exprMap[pointer.id] ?? fallbackExprMap[pointer.id];
  if (!exprMapValue) {
    return null;
  }
  let result: Expression = getExpressionFromMapAndValue(
    exprMap,
    fallbackExprMap,
    exprMapValue
  )!;
  const variableData = [
    ...(Object.values(pointer.variablesExpressionData || {}).sort(
      // Sort in descending order of weights.
      (a, b) => b.weight - a.weight
    ) || []),
  ];
  variableData.forEach(({ id, name, arg }) => {
    const argExpr = getExpressionFromMap(exprMap, fallbackExprMap, arg)!;
    result = {
      id: uniqueId(),
      type: "ApplicationExpression",
      valueType: result.valueType,
      arguments: [argExpr],
      function: {
        id: uniqueId(),
        type: "FunctionExpression",
        valueType: {
          type: "FunctionValueType",
          parameterValueTypes: [argExpr.valueType],
          returnValueType: result.valueType,
        },
        parameters: [{ id, name }],
        body: result,
      },
    };
  });

  return result;
}

export function getExpressionFromMapAndValue(
  exprMap: ExpressionMap,
  fallbackExprMap: ExpressionMap,
  exprMapValue: ExpressionMapValue
): Expression | null {
  switch (exprMapValue.type) {
    case "NoOpExpression":
    case "BooleanExpression":
    case "StringExpression":
    case "IntExpression":
    case "FloatExpression":
    case "RegexExpression":
    case "EnumExpression":
    case "VariableExpression":
      return exprMapValue;

    case "ObjectExpressionMapValue":
      return {
        ...exprMapValue,
        type: "ObjectExpression",
        fields: getRecordOfExpressionsFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.fields
        ),
      };
    case "GetFieldExpressionMapValue":
      return {
        ...exprMapValue,
        type: "GetFieldExpression",
        object: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.object
        ),
      };
    case "UpdateObjectExpressionMapValue":
      return {
        ...exprMapValue,
        type: "UpdateObjectExpression",
        object: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.object
        ),
        updates: getRecordOfExpressionsFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.updates
        ),
      };
    case "ListExpressionMapValue": {
      const { itemsWeights: weights, ...expr } = exprMapValue;
      return {
        ...expr,
        type: "ListExpression",
        items: getArrayOfExpressionsFromMap(
          exprMap,
          fallbackExprMap,
          expr.items,
          weights
        ),
      };
    }

    case "SwitchExpressionMapValue": {
      const { casesWeights: weights, ...expr } = exprMapValue;
      return {
        ...expr,
        type: "SwitchExpression",
        control: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.control
        ),
        cases: Object.entries(exprMapValue.cases)
          .map(([id, { when, then }]) => ({
            id,
            when: getExpressionFromMap(exprMap, fallbackExprMap, when),
            then: getExpressionFromMap(exprMap, fallbackExprMap, then),
          }))
          .sort((a, b) => weights[a.when!.id] - weights[b.when!.id]),
        default: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.default
        ),
      };
    }

    case "EnumSwitchExpressionMapValue":
      return {
        ...exprMapValue,
        type: "EnumSwitchExpression",
        control: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.control
        ),
        cases: getRecordOfExpressionsFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.cases
        ),
      };
    case "ArithmeticExpressionMapValue":
      return {
        ...exprMapValue,
        type: "ArithmeticExpression",
        a: getExpressionFromMap(exprMap, fallbackExprMap, exprMapValue.a),
        b: getExpressionFromMap(exprMap, fallbackExprMap, exprMapValue.b),
      };
    case "ComparisonExpressionMapValue":
      return {
        ...exprMapValue,
        type: "ComparisonExpression",
        a: getExpressionFromMap(exprMap, fallbackExprMap, exprMapValue.a),
        b: getExpressionFromMap(exprMap, fallbackExprMap, exprMapValue.b),
      };
    case "RoundNumberExpressionMapValue":
      return {
        ...exprMapValue,
        type: "RoundNumberExpression",
        number: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.number
        ),
      };
    case "StringifyNumberExpressionMapValue":
      return {
        ...exprMapValue,
        type: "StringifyNumberExpression",
        number: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.number
        ),
      };
    case "StringConcatExpressionMapValue":
      return {
        ...exprMapValue,
        type: "StringConcatExpression",
        strings: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.strings
        ),
      };
    case "GetUrlQueryParameterExpressionMapValue":
      return {
        ...exprMapValue,
        type: "GetUrlQueryParameterExpression",
        url: getExpressionFromMap(exprMap, fallbackExprMap, exprMapValue.url),
        queryParameterName: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.queryParameterName
        ),
      };
    case "SplitExpressionMapValue":
      return {
        ...exprMapValue,
        type: "SplitExpression",
        expose: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.expose
        ),
        unitId: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.unitId
        ),
        dimensionMapping:
          exprMapValue.dimensionMapping.type ===
          "ExpressionMapValueDiscreteDimensionMapping"
            ? {
                ...exprMapValue.dimensionMapping,
                type: "discrete",
                cases: getRecordOfExpressionsFromMap(
                  exprMap,
                  fallbackExprMap,
                  exprMapValue.dimensionMapping.cases
                ),
              }
            : {
                ...exprMapValue.dimensionMapping,
                type: "continuous",
                function: getExpressionFromMap(
                  exprMap,
                  fallbackExprMap,
                  exprMapValue.dimensionMapping.function
                ),
              },
        eventPayload: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.eventPayload
        ),
        featuresMapping: getRecordOfExpressionsFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.featuresMapping
        ),
      };
    case "LogEventExpressionMapValue":
      return {
        ...exprMapValue,
        type: "LogEventExpression",
        unitId: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.unitId
        ),
        eventPayload: getExpressionFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.eventPayload
        ),
        featuresMapping: getRecordOfExpressionsFromMap(
          exprMap,
          fallbackExprMap,
          exprMapValue.featuresMapping
        ),
      };
    case "FunctionExpressionMapValue":
      return {
        ...exprMapValue,
        type: "FunctionExpression",
        /**
         * Ensure that valueType.parameterValueTypes and parameters are arrays
         * as the diff function may have changed them into index-keyed objects.
         */
        valueType: {
          ...exprMapValue.valueType,
          parameterValueTypes: Object.values(
            exprMapValue.valueType.parameterValueTypes
          ),
        },
        parameters: Object.values(exprMapValue.parameters),
        body: getExpressionFromMap(exprMap, fallbackExprMap, exprMapValue.body),
      };

    default: {
      const neverExpression: never = exprMapValue;
      throw new Error(`unexpected expression: ${neverExpression}`);
    }
  }
}

function getArrayOfExpressionsFromMap(
  exprMap: ExpressionMap,
  fallbackExprMap: ExpressionMap,
  record: Record<string, ExpressionMapPointer>,
  weights: { [id: string]: number }
): (Expression | null)[] {
  return Object.entries(
    getRecordOfExpressionsFromMap(exprMap, fallbackExprMap, record)
  )
    .sort(([id1], [id2]) => weights[id1] - weights[id2])
    .map(([, itemExpr]) => itemExpr);
}

function getRecordOfExpressionsFromMap(
  exprMap: ExpressionMap,
  fallbackExprMap: ExpressionMap,
  record: Record<string, ExpressionMapPointer>
): Record<string, Expression | null> {
  return Object.fromEntries(
    Object.entries(record).map(([fieldName, fieldExpr]) => [
      fieldName,
      getExpressionFromMap(exprMap, fallbackExprMap, fieldExpr),
    ])
  );
}
